/**
 * Extracts a user-friendly error message from an API error response
 *
 * @param error - The error object from an API call
 * @param fallbackMessage - The message to return if no specific error message is found
 * @returns A user-friendly error message string
 *
 * @example
 * ```ts
 * const errorMessage = getApiErrorMessage(error, t('common.errors.generic'))
 * ```
 */
export const getApiErrorMessage = (
  error: unknown,
  fallbackMessage: string,
): string | undefined => {
  if (!error) return undefined

  // Handle API errors
  if (typeof error === 'object') {
    // Check for body.error.message (nested error format)
    if ('body' in error && typeof error.body === 'object' && error.body) {
      const body = error.body as Record<string, unknown>
      if (
        typeof body.error === 'object' &&
        body.error !== null &&
        'message' in body.error &&
        typeof body.error.message === 'string'
      ) {
        return body.error.message
      }
      // Check for body.message (flat error format)
      if (typeof body.message === 'string') {
        return body.message
      }
    }
    // Check for error.message directly on error
    if ('error' in error && typeof error.error === 'object' && error.error) {
      const err = error.error as Record<string, unknown>
      if (typeof err.message === 'string') {
        return err.message
      }
    }
    // Check for message directly on error
    if ('message' in error && typeof error.message === 'string') {
      return error.message
    }
  }

  return fallbackMessage
}

export const getApiErrorStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined

  if ('status' in error && typeof error.status === 'number') {
    return error.status
  }

  return undefined
}

export const getApiErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') return undefined

  if ('body' in error && typeof error.body === 'object' && error.body) {
    const body = error.body as Record<string, unknown>
    if (
      typeof body.error === 'object' &&
      body.error !== null &&
      'code' in body.error &&
      typeof body.error.code === 'string'
    ) {
      return body.error.code
    }
  }

  return undefined
}
